import React from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { selectOptionWithAvatar } from '@atlassian/jira-portfolio-3-common/src/select/index.tsx';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types.tsx';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types.tsx';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import SearchablePopupSelect from '../../popup-select/searchable/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

function ProjectFilter({
	projects,
	projectFilter,
	setProjectFilter,
	onFilterChange,
	isOpen = false,
}: Props) {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const options: OptionType[] = projects.map((project) => ({
		label: project.name,
		value: project.id.toString(),
		icon: project.avatarUrl,
	}));
	const values = options.filter(
		(option) => projectFilter && projectFilter.includes(option.value.toString()),
	);

	const renderProject = (option: OptionType) =>
		selectOptionWithAvatar({
			icon: option.icon,
			label: option.label,
		});

	const filterOption = (option: OptionType, rawInput: string): boolean => {
		if (option.label === undefined) return false;
		const query = rawInput.toLowerCase();
		return option.label.toString().toLowerCase().includes(query);
	};

	return (
		<SearchablePopupSelect
			label={intl.formatMessage(commonMessages.project)}
			isMulti
			isOpen={isOpen}
			value={values}
			searchPlaceholder={intl.formatMessage(messages.placeholder)}
			options={options}
			formatOptionLabel={renderProject}
			onChange={(changedOptions) => {
				if (changedOptions) {
					setProjectFilter(changedOptions.map((option: OptionType) => option.value));
				} else {
					setProjectFilter([]);
				}
				onFilterChange();

				const [actionSubject, action] =
					PRODUCT_ANALYTICS_EVENT_NAMES.UPDATED_DEPENDENCY_PROJECT_FILTER.split(' ');
				fireUIAnalytics(
					createAnalyticsEvent({
						action,
						actionSubject,
					}),
					{
						projectsCount: changedOptions ? changedOptions.length : 0,
					},
				);
			}}
			filterOption={filterOption}
			testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.filters.project"
		/>
	);
}

export default ProjectFilter;
